import Accounting from 'accounting';
import { isNumber, has, get, assign } from 'lodash';

import Settings from 'utils/Settings';

import { PRICE_FORMAT } from './constants';
import { CURRENCIES } from './currencies';

const DEFAULT_ISO_CODE = 'USD';
const FORMAT_DEFAULTS = { currencySuffix: true };

class Money {
  constructor(subUnits = 0, isoCode) {
    if (!isNumber(subUnits) || !(subUnits % 1 === 0)) {
      throw new Error('Money must be in subUnits represented by a whole number.');
    }

    if (isoCode && !has(CURRENCIES, isoCode)) {
      throw new Error('Invalid isoCode provided.');
    }

    this.subUnits = subUnits;
    this.isoCode = isoCode || Settings.currency || DEFAULT_ISO_CODE;
  }

  static getFormat(isoCode, format) {
    return get(CURRENCIES, [isoCode, format]);
  }

  get units() {
    return this.subUnits / 100;
  }

  get isPositive() {
    return this.subUnits > 0;
  }

  get isNegative() {
    return this.subUnits < 0;
  }

  get isZero() {
    return this.subUnits === 0;
  }

  get isNonZero() {
    return this.subUnits !== 0;
  }

  isGreaterThan(moneyInstance) {
    return this.subUnits > moneyInstance.subUnits;
  }

  isGreaterThanOrEqualTo(moneyInstance) {
    return this.subUnits >= moneyInstance.subUnits;
  }

  isLessThan(moneyInstance) {
    return this.subUnits < moneyInstance.subUnits;
  }

  isLessThanOrEqualTo(moneyInstance) {
    return this.subUnits <= moneyInstance.subUnits;
  }

  isEqualTo(moneyInstance) {
    return this.subUnits === moneyInstance.subUnits;
  }

  add(subUnits) {
    const sum = this.subUnits + subUnits;

    return new Money(sum, this.isoCode);
  }

  subtract(subUnits) {
    const sum = this.subUnits - subUnits;

    return new Money(sum, this.isoCode);
  }

  multiplyBy(multiple, rounding = 'round') {
    let product = this.subUnits * multiple;

    if (rounding && Math[rounding]) {
      product = Math[rounding](product);
    }

    return new Money(product, this.isoCode);
  }

  divideBy(divisor, rounding = 'round') {
    let quotient = this.subUnits / divisor;

    if (rounding && Math[rounding]) {
      quotient = Math[rounding](quotient);
    }

    return new Money(quotient, this.isoCode);
  }

  format(format = PRICE_FORMAT.units, options = {}) {
    const formatOptions = assign({}, FORMAT_DEFAULTS, options);
    const currencyFormat = Money.getFormat(this.isoCode, format);
    const formatted = Accounting.formatMoney(this.units, currencyFormat);
    const currencySuffix = formatOptions.currencySuffix ? currencyFormat.suffix : '';

    return `${formatted}${currencySuffix}`.trim();
  }

  getDecimalFormat() {
    const _price = `${this.subUnits}`;
    return `${_price.substring(0, _price.length - 2)}.${_price.substring(_price.length - 2)}`;
  }
}

export default Money;

export { CURRENCIES, PRICE_FORMAT };
